import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from './config.js';
import noImage from '../assets/no-image.png';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Button,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  LinearProgress,
  Tooltip,
  TablePagination,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  ViewModule as CardViewIcon,
  ViewList as TableViewIcon,
  Download as DownloadIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  ImageNotSupported as NoImageIcon,
  PhotoLibrary as ImagesIcon,
  ShoppingBasket as ShoppingBasketIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { addQuotationItem } from './quotationApi.js';

const buildCacheKey = (search, brand, sort, page, rows) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (brand && brand !== 'All') params.set('brand', brand);
  if (sort && sort !== 'None') params.set('sort', sort);
  if (page > 1) params.set('page', page);
  if (rows !== 20) params.set('rows', rows);
  const s = params.toString();
  return s ? '?' + s : '';
};

// Rows per page options
const rowsPerPageOptions = [10, 20, 50, 100, 200];

// Catalog card image. product.images[0] comes straight from the product-list
// API and carries small_url/medium_url (locally cached, verified Oracle
// images) alongside the legacy image_url - no live Oracle fetch needed.
// Falls back to image_url while an item awaits verification, then to the
// local "no image" placeholder if there's nothing at all.
const OracleProductImage = ({ product, size = 'medium' }) => {
  const height = size === 'small' ? 60 : 300;
  const firstImage = product.images && product.images.length > 0 ? product.images[0] : null;
  const imageUrl =
    firstImage &&
    (size === 'small'
      ? firstImage.small_url || firstImage.medium_url || firstImage.image_url
      : firstImage.medium_url || firstImage.original_url || firstImage.image_url);

  if (imageUrl) {
    return (
      <Box
        sx={{
          width: '100%',
          height,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: 'grey.100',
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: size === 'small' ? 60 : '100%',
        height: size === 'small' ? 60 : 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: size === 'small' ? 1 : 0,
        overflow: 'hidden',
      }}
    >
      <img
        src={noImage}
        alt="No Image"
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </Box>
  );
};

const ProductSearchPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  // Parse URL params and sessionStorage cache ONCE at initialization time so
  // useState gets the right values on the first render — no mount effect needed,
  // no state update after mount, no spurious re-fetch.
  const _init = useRef(null);
  if (!_init.current) {
    const sp = new URLSearchParams(window.location.search);
    const urlSearch = sp.get('search') ? decodeURIComponent(sp.get('search')) : '';
    const urlBrand = sp.get('brand') || 'All';
    const urlSort = sp.get('sort') || 'None';
    const urlPage = parseInt(sp.get('page') || '1', 10);
    const urlRows = parseInt(sp.get('rows') || '20', 10);
    const urlHasSearched = !!(sp.get('search') || sp.get('brand') || sp.get('sort'));

    let cachedProducts = [], cachedTotalPages = 1, cachedTotalCount = 0, hasCache = false;
    try {
      const raw = sessionStorage.getItem('productSearchCache');
      if (raw && window.location.search) {
        const c = JSON.parse(raw);
        if (c.key === window.location.search) {
          cachedProducts = c.products;
          cachedTotalPages = c.totalPages;
          cachedTotalCount = c.totalCount;
          hasCache = true;
        }
      }
    } catch {}

    _init.current = { urlSearch, urlBrand, urlSort, urlPage, urlRows, urlHasSearched, cachedProducts, cachedTotalPages, cachedTotalCount, hasCache };
  }
  const init = _init.current;

  const [products, setProducts] = useState(init.cachedProducts);
  const [searchTerm, setSearchTerm] = useState(init.urlSearch);   // committed — triggers fetch
  const [inputValue, setInputValue] = useState(init.urlSearch);   // live text field value
  const [selectedBrand, setSelectedBrand] = useState(init.urlBrand);
  const [sortBy, setSortBy] = useState(init.urlSort);
  const [currentPage, setCurrentPage] = useState(init.urlPage);
  const [totalPages, setTotalPages] = useState(init.cachedTotalPages);
  const [totalCount, setTotalCount] = useState(init.cachedTotalCount);
  const [hasSearched, setHasSearched] = useState(init.urlHasSearched);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('card');
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Export states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Image download states
  const [imageDownloadDialogOpen, setImageDownloadDialogOpen] = useState(false);
  const [imageDownloading, setImageDownloading] = useState(false);
  const [imageDownloadProgress, setImageDownloadProgress] = useState(0);
  const [imageDownloadError, setImageDownloadError] = useState(null);
  const [imageDownloadSuccess, setImageDownloadSuccess] = useState(false);

  // Add to Quotation states
  const [quotationDialogOpen, setQuotationDialogOpen] = useState(false);
  const [addingToQuotation, setAddingToQuotation] = useState(false);
  const [quotationError, setQuotationError] = useState(null);
  const [quotationSuccessMessage, setQuotationSuccessMessage] = useState(null);
  const [addingToBasketId, setAddingToBasketId] = useState(null);

  // Rows per page state
  const [rowsPerPage, setRowsPerPage] = useState(init.urlRows);

  const [brands, setBrands] = useState(['All']);

  useEffect(() => {
    const token = localStorage.getItem('access');
    axios.get(`${API_BASE_URL}/api/brands/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setBrands(res.data))
      .catch(() => {});
  }, []);

  // Keep URL in sync with search state so back-navigation restores it
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedBrand && selectedBrand !== 'All') params.set('brand', selectedBrand);
    if (sortBy && sortBy !== 'None') params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', currentPage);
    if (rowsPerPage !== 20) params.set('rows', rowsPerPage);
    navigate({ search: params.toString() }, { replace: true });
  }, [searchTerm, selectedBrand, sortBy, currentPage, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch products from backend
  useEffect(() => {
    const fetchProducts = async () => {
      // If current params match the cache, results are already in state — skip the API call.
      // This handles back-navigation correctly regardless of how many times the effect runs.
      const cacheKey = buildCacheKey(searchTerm, selectedBrand, sortBy, currentPage, rowsPerPage);
      try {
        const raw = sessionStorage.getItem('productSearchCache');
        if (raw) {
          const c = JSON.parse(raw);
          if (c.key === cacheKey) return;
        }
      } catch {}

      setLoading(true);
      try {
        const params = {
          search: searchTerm || undefined,
          brand: selectedBrand !== 'All' ? selectedBrand : undefined,
          sort_by: sortBy,
          page: currentPage,
          page_size: rowsPerPage,
        };
        const token = localStorage.getItem("access");
        if (!token) throw new Error("No access token found. Please login.");
        const response = await axios.get(`${API_BASE_URL}/api/products/`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetchedProducts = response.data.results;
        const fetchedCount = response.data.count;
        const fetchedTotalPages = Math.ceil(fetchedCount / rowsPerPage);

        setProducts(fetchedProducts);
        setTotalCount(fetchedCount);
        setTotalPages(fetchedTotalPages);
        setSelectedProducts(new Set());
        setSelectAll(false);

        sessionStorage.setItem('productSearchCache', JSON.stringify({
          key: cacheKey,
          products: fetchedProducts,
          totalPages: fetchedTotalPages,
          totalCount: fetchedCount,
        }));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    if (searchTerm || hasSearched) {
      fetchProducts();
    } else {
      setProducts([]);
      setTotalPages(1);
      setTotalCount(0);
    }
  }, [searchTerm, selectedBrand, sortBy, currentPage, rowsPerPage, hasSearched]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSearch = () => {
    setSearchTerm(inputValue);
    setCurrentPage(1);
    if (inputValue.trim()) setHasSearched(true);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleBrandChange = (value) => {
    setSelectedBrand(value);
    setCurrentPage(1);
    setHasSearched(true);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1);
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`);
  };

  // Selection handlers
  const handleSelectProduct = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
    setSelectAll(newSelected.size === products.length && products.length > 0);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
    setSelectAll(!selectAll);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setInputValue('');
    setSelectedBrand('All');
    setSortBy('None');
    setHasSearched(false);
    setProducts([]);
    setCurrentPage(1);
    setTotalPages(1);
    setTotalCount(0);
    setSelectedProducts(new Set());
    setSelectAll(false);
    navigate({ search: '' }, { replace: true });
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  // Export handler
  const handleExportClick = () => {
    if (selectedProducts.size === 0) {
      setExportError('Please select at least one product to export');
      return;
    }
    setExportDialogOpen(true);
  };

  const handleExportConfirm = async () => {
    setExporting(true);
    setExportDialogOpen(false);
    setExportProgress(10);
    
    try {
      const token = localStorage.getItem("access"); // use access token
      if (!token) throw new Error("No access token found. Please login.");
      const response = await axios.post(
        `${API_BASE_URL}/api/export-products/`,
        {
          product_ids: Array.from(selectedProducts), // this is the request payload
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setExportProgress(percentCompleted);
            }
          },
        }
      );

      setExportProgress(90);

      // Check if response is an error (even with blob type)
      if (response.data.type === 'application/json') {
        const text = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsText(response.data);
        });
        
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || 'Export failed');
        } catch (e) {
          throw new Error('Export failed with unknown error');
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'products_with_images.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setExportProgress(100);
      setTimeout(() => {
        setExportSuccess(true);
        setExporting(false);
        setExportProgress(0);
      }, 500);
      
      setExportError(null);
    } catch (error) {
      console.error('Export error:', error);
      setExportError(error.message || 'Failed to export products. Please try again.');
      setExporting(false);
      setExportProgress(0);
    }
  };

  // Add to Quotation handler (bulk) - defaults every selected item to the
  // In-line Standard price category; category/quantity can be adjusted
  // afterward from the Quotation basket itself.
  const handleAddToQuotationClick = () => {
    if (selectedProducts.size === 0) {
      setQuotationError('Please select at least one product to add to your quotation');
      return;
    }
    setQuotationDialogOpen(true);
  };

  const handleAddToQuotationConfirm = async () => {
    setQuotationDialogOpen(false);
    setAddingToQuotation(true);
    setQuotationError(null);

    const selectedList = products.filter((p) => selectedProducts.has(p.id));
    const results = await Promise.allSettled(
      selectedList.map((p) => addQuotationItem({
        sku: p.item_number, priceCategory: 'inline_standard', quantity: 1,
      }))
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - succeeded;

    setAddingToQuotation(false);
    if (succeeded > 0) {
      setQuotationSuccessMessage(
        failed > 0
          ? `Added ${succeeded} item(s) to your quotation (${failed} had no current price and were skipped).`
          : `Added ${succeeded} item(s) to your quotation.`
      );
    }
    if (failed > 0 && succeeded === 0) {
      setQuotationError('None of the selected items have a current In-line Standard price to quote.');
    }
  };

  // Quick "Add to Basket" from a single product card - same In-line Standard
  // default as the bulk action above.
  const handleQuickAddToBasket = async (product, event) => {
    event.stopPropagation();
    setAddingToBasketId(product.id);
    try {
      await addQuotationItem({ sku: product.item_number, priceCategory: 'inline_standard', quantity: 1 });
      setQuotationSuccessMessage(`Added ${product.item_number} to your basket.`);
    } catch (err) {
      const message = err?.response?.data?.error
        || `Could not add ${product.item_number} - it has no current In-line Standard price.`;
      setQuotationError(message);
    } finally {
      setAddingToBasketId(null);
    }
  };

  // Image download handler
  const handleImageDownloadClick = () => {
    if (selectedProducts.size === 0) {
      setImageDownloadError('Please select at least one product to download images');
      return;
    }
    setImageDownloadDialogOpen(true);
  };


  // Image download handler - with better error handling for URL downloads
  const handleImageDownloadConfirm = async () => {
    setImageDownloading(true);
    setImageDownloadDialogOpen(false);
    setImageDownloadProgress(10);
    
    try {
      console.log('Downloading images for products:', Array.from(selectedProducts));

      const token = localStorage.getItem("access");
      if (!token) throw new Error("No access token found. Please login.");
      const response = await axios.post(
        `${API_BASE_URL}/api/download-product-images/`,
        {
          product_ids: Array.from(selectedProducts),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setImageDownloadProgress(percentCompleted);
              console.log('Download progress:', percentCompleted, 'Received:', progressEvent.loaded, 'Total:', progressEvent.total);
            } else if (progressEvent.loaded > 0) {
              // If total is unknown, show indeterminate progress
              console.log('Downloaded:', progressEvent.loaded, 'bytes');
            }
          },
          timeout: 300000, // 5 minute timeout for large downloads
        }
      );

      console.log('Response received:', response);
      console.log('Response headers:', response.headers);
      console.log('Response data type:', response.data.type);
      console.log('Response data size:', response.data.size);

      setImageDownloadProgress(90);

      // Check if response is an error (check content type)
      if (response.data.type === 'application/json') {
        // This is an error response
        const reader = new FileReader();
        reader.onload = () => {
          try {
            console.log('Error response text:', reader.result);
            const errorData = JSON.parse(reader.result);
            setImageDownloadError(errorData.error || 'Image download failed');
            if (errorData.details) {
              console.error('Error details:', errorData.details);
            }
          } catch (e) {
            console.error('Error parsing error response:', e);
            setImageDownloadError('Image download failed with unknown error');
          }
        };
        reader.readAsText(response.data);
        return;
      }

      // Check if we got a valid blob
      if (!response.data || response.data.size === 0) {
        console.error('Received empty file');
        throw new Error('Server returned an empty file. No images could be downloaded from the URLs.');
      }

      console.log('Received valid blob of size:', response.data.size);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename
      const contentDisposition = response.headers['content-disposition'];
      let filename = `product_images_${selectedProducts.size}_products.zip`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      console.log('Triggering download for:', filename);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setImageDownloadProgress(100);
      setTimeout(() => {
        setImageDownloadSuccess(true);
        setImageDownloading(false);
        setImageDownloadProgress(0);
      }, 500);
      
      setImageDownloadError(null);
    } catch (error) {
      console.error('Image download error:', error);
      
      // Handle error response
      if (error.response) {
        console.error('Error response:', error.response);
        if (error.response.data instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              console.log('Error response text:', reader.result);
              const errorData = JSON.parse(reader.result);
              setImageDownloadError(errorData.error || 'Failed to download images');
              if (errorData.details) {
                console.error('Error details:', errorData.details);
              }
            } catch (e) {
              console.error('Error parsing error response:', e);
              setImageDownloadError(error.message || 'Failed to download images. Please try again.');
            }
          };
          reader.readAsText(error.response.data);
        } else {
          setImageDownloadError(error.message || 'Failed to download images. Please try again.');
        }
      } else {
        setImageDownloadError(error.message || 'Failed to download images. Please try again.');
      }
      
      setImageDownloading(false);
      setImageDownloadProgress(0);
    }
  };

  // Render card view with checkboxes
  const renderCardView = () => (
    <Grid container spacing={3} justifyContent="center">
      {products.map(product => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={product.id} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ 
            height: '100%', 
            width: '100%', 
            maxWidth: 400, 
            minHeight: 450, 
            display: 'flex', 
            flexDirection: 'column', 
            transition: 'all 0.3s ease-in-out', 
            '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
            position: 'relative',
          }}>
            {/* Selection Checkbox */}
            <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
              <Checkbox
                checked={selectedProducts.has(product.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleSelectProduct(product.id);
                }}
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
              />
            </Box>
            
            <CardActionArea onClick={() => handleProductClick(product)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ position: 'relative', width: '100%' }}>
                <OracleProductImage product={product} />
              </Box>

              <CardContent sx={{ flexGrow: 1, width: '95%', display: 'flex', flexDirection: 'column'}}>
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '1.1rem', lineHeight: 1.2, minHeight: '2.4rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {product.product_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                  {product.brand}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, minHeight: '40px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                  {product.description || 'No description available'}
                </Typography>
                <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                  UPC:{product.upc_code || 'No UPC Code'}
                </Typography>
              </CardContent>
            </CardActionArea>

            {/* Fix: this row was previously nested inside CardActionArea, producing an invalid <button> inside <button> (CardActionArea + Button both render as <button>). Moved outside as a sibling. */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pb: 2, pt: 1, gap: 1 }}>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '1.1rem' }}>
                {product.item_number}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Add to Basket">
                  <span>
                    <IconButton
                      color="primary"
                      size={isMobile ? "small" : "medium"}
                      onClick={(e) => handleQuickAddToBasket(product, e)}
                      disabled={addingToBasketId === product.id}
                    >
                      {addingToBasketId === product.id
                        ? <CircularProgress size={20} />
                        : <ShoppingBasketIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Button variant="contained" size={isMobile ? "small" : "medium"} startIcon={<ViewIcon />} onClick={() => handleProductClick(product)}>
                  View
                </Button>
              </Stack>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // Render table view with checkboxes
  const renderTableView = () => (
    <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
      <Table sx={{ minWidth: 650 }} aria-label="products table">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'primary.main' }}>
            <TableCell padding="checkbox" sx={{ color: 'white' }}>
              <Checkbox
                checked={selectAll}
                indeterminate={selectedProducts.size > 0 && selectedProducts.size < products.length}
                onChange={handleSelectAll}
                sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
              />
            </TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Image</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Product Name</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Brand</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Item Number</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>UPC</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product) => (
            <TableRow
              key={product.id}
              sx={{ 
                '&:hover': { 
                  backgroundColor: 'action.hover',
                },
                backgroundColor: selectedProducts.has(product.id) ? 'action.selected' : 'inherit',
                cursor: 'pointer'
              }}
              onClick={() => handleProductClick(product)}
            >
              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedProducts.has(product.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectProduct(product.id);
                  }}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ width: 60, height: 60 }}>
                  <OracleProductImage product={product} size="small" />
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {product.product_name}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip label={product.brand || 'N/A'} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="primary" fontWeight="bold">
                  {product.item_number}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {product.upc_code}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    maxWidth: 250,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {product.description || 'No description available'}
                </Typography>
              </TableCell>
              <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ViewIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProductClick(product);
                  }}
                >
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant={isMobile ? "h5" : "h4"}
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          {searchTerm ? `Search Results for "${searchTerm}"` : 'Product Catalog'}
        </Typography>
        <Typography 
          variant={isMobile ? "body1" : "h6"}
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {searchTerm 
            ? `Found ${totalCount} products matching your search`
            : 'Search for products to see results'
          }
        </Typography>
      </Box>

      {/* Search and Filter Section */}
      <Box sx={{ mb: 4, p: isMobile ? 2 : 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Input */}
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search or paste item numbers (e.g. 13202.12R, 3234.13)"
              variant="outlined"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleSearchKeyDown}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      variant="contained"
                      onClick={handleSearch}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        mr: -1.5,
                        borderRadius: '0 4px 4px 0',
                        height: isMobile ? '40px' : '56px',
                        px: 2.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' },
                      }}
                    >
                      Search
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Brand Filter */}
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Brand</InputLabel>
              <Select
                value={selectedBrand}
                label="Brand"
                onChange={(e) => handleBrandChange(e.target.value)}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                {brands.map(brand => (
                  <MenuItem key={brand} value={brand}>
                    {brand}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Sort By */}
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={handleSortChange}
              >
                <MenuItem value="None">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="brand">Brand</MenuItem>
                <MenuItem value="item_number">Item Number</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Rows Per Page Selector */}
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Rows per page</InputLabel>
              <Select
                value={rowsPerPage}
                label="Rows per page"
                onChange={handleRowsPerPageChange}
              >
                {rowsPerPageOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* View Toggle */}
          <Grid item xs={6} sm={2} md={1}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              aria-label="view mode"
              size={isMobile ? "small" : "medium"}
              sx={{ 
                height: isMobile ? '40px' : '56px',
                width: '100%'
              }}
            >
              <ToggleButton value="card" aria-label="card view" sx={{ flex: 1 }}>
                <CardViewIcon />
              </ToggleButton>
              <ToggleButton value="table" aria-label="table view" sx={{ flex: 1 }}>
                <TableViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Image Download Button */}
          <Grid item xs={6} sm={2} md={1}>
            <Tooltip title={selectedProducts.size === 0 ? "Select products to download images" : ""}>
              <span>
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="secondary"
                  onClick={handleImageDownloadClick}
                  disabled={selectedProducts.size === 0 || imageDownloading}
                  startIcon={<ImagesIcon />}
                  size={isMobile ? "small" : "medium"}
                  sx={{ height: isMobile ? '40px' : '56px' }}
                >
                  Images
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* Export Button */}
          <Grid item xs={6} sm={2} md={1}>
            <Tooltip title={selectedProducts.size === 0 ? "Select products to export" : ""}>
              <span>
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="success"
                  onClick={handleExportClick}
                  disabled={selectedProducts.size === 0 || exporting}
                  startIcon={<DownloadIcon />}
                  size={isMobile ? "small" : "medium"}
                  sx={{ height: isMobile ? '40px' : '56px' }}
                >
                  Excel
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* Add to Quotation Button */}
          <Grid item xs={6} sm={2} md={1}>
            <Tooltip title={selectedProducts.size === 0 ? "Select products to add to your quotation" : ""}>
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleAddToQuotationClick}
                  disabled={selectedProducts.size === 0 || addingToQuotation}
                  startIcon={addingToQuotation ? <CircularProgress size={16} color="inherit" /> : <ShoppingBasketIcon />}
                  size={isMobile ? "small" : "medium"}
                  sx={{ height: isMobile ? '40px' : '56px' }}
                >
                  Quote
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* Clear Filters */}
          <Grid item xs={6} sm={2} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              size={isMobile ? "small" : "medium"}
              sx={{ height: isMobile ? '40px' : '56px' }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Selection Info Bar */}
      {selectedProducts.size > 0 && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium' }}>
              {selectedProducts.size} product(s) selected
            </Typography>
            <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
              {selectedProducts.size} of {totalCount} total products
            </Typography>
          </Box>
          <Button 
            size="small" 
            variant="text" 
            onClick={() => setSelectedProducts(new Set())}
            sx={{ color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            Clear Selection
          </Button>
        </Box>
      )}

      {/* Export Progress */}
      {exporting && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <Typography variant="body2" gutterBottom>
            Exporting products... {exportProgress}%
          </Typography>
          <LinearProgress variant="determinate" value={exportProgress} sx={{ height: 8, borderRadius: 4 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Generating Excel file with embedded images...
          </Typography>
        </Box>
      )}

      {/* Image Download Progress */}
      {/* TEMP POC note: images are now fetched live from Oracle Fusion and zipped server-side before anything
          is sent back, so there's no real byte progress to report until the very end. An indeterminate bar
          avoids the misleading "stuck at 10%" look a determinate bar had. */}
      {imageDownloading && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <Typography variant="body2" gutterBottom>
            {imageDownloadProgress >= 90 ? 'Finalizing your download...' : 'Preparing your images...'}
          </Typography>
          <LinearProgress variant="indeterminate" sx={{ height: 8, borderRadius: 4 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This can take a little longer for products with many images — please keep this tab open.
          </Typography>
        </Box>
      )}

      {/* Results Info */}
      {products.length > 0 && !exporting && !imageDownloading && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Showing {products.length} of {totalCount} products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>
        </Box>
      )}

      {/* Products Display */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LinearProgress sx={{ maxWidth: 400, mx: 'auto', mb: 2 }} />
          <Typography>Loading products...</Typography>
        </Box>
      ) : products.length > 0 ? (
        <>
          {/* Dynamic View Rendering */}
          {viewMode === 'card' ? renderCardView() : renderTableView()}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination 
                count={totalPages} 
                page={currentPage} 
                onChange={handlePageChange}
                color="primary" 
                size={isMobile ? "small" : "medium"} 
                showFirstButton 
                showLastButton
              />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          {hasSearched ? (
            <>
              <Typography variant="h6" color="text.secondary" gutterBottom>No products found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Try adjusting your search criteria or filters</Typography>
              <Button variant="contained" onClick={clearFilters}>Clear All Filters</Button>
            </>
          ) : (
            <>
              <Typography variant="h6" color="text.secondary" gutterBottom>Start searching for products</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Use the search bar above to find products in our catalog</Typography>
            </>
          )}
        </Box>
      )}

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Products to Excel</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            You are about to export <strong>{selectedProducts.size}</strong> product(s) to an Excel file.
          </DialogContentText>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>The Excel file will include:</strong>
            </Typography>
            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
              <li>All product data (item number, name, brand, description, etc.)</li>
              <li>Primary images embedded directly in column A</li>
              <li>Formatted headers and organized columns</li>
            </ul>
            <Typography variant="caption" color="text.secondary">
              Note: Images will be embedded in column A. If images fail to load, error messages will appear instead.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExportConfirm} variant="contained" color="success">
            Export to Excel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Download Dialog */}
      <Dialog open={imageDownloadDialogOpen} onClose={() => setImageDownloadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Download Product Images</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            You are about to download all images for <strong>{selectedProducts.size}</strong> product(s).
          </DialogContentText>
          
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>The ZIP file will contain:</strong>
            </Typography>
            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
              <li>Images grouped in folders by item number</li>
              <li>Each folder named with the item number (e.g., SKU12345/)</li>
              <li>Images named sequentially: SKU_01.jpg, SKU_02.jpg, etc.</li>
              <li>A README file with download report if any images fail</li>
            </ul>
          </Alert>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Large downloads may take a few moments. The server will download and package all images before sending the ZIP file.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDownloadDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleImageDownloadConfirm} variant="contained" color="secondary">
            Download Images
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Quotation Dialog */}
      <Dialog open={quotationDialogOpen} onClose={() => setQuotationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add to Quotation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add <strong>{selectedProducts.size}</strong> product(s) to your quotation?
          </DialogContentText>
          <Alert severity="info">
            <Typography variant="body2">
              Each item is added at quantity 1 using the current <strong>In-line Standard</strong> price.
              Any item without a current In-line Standard price will be skipped. You can adjust quantity,
              price category, or remove items from your quotation basket afterward.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuotationDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddToQuotationConfirm} variant="contained" color="primary">
            Add to Quotation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!exportError || !!imageDownloadError || !!quotationError}
        autoHideDuration={6000}
        onClose={() => {
          setExportError(null);
          setImageDownloadError(null);
          setQuotationError(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            setExportError(null);
            setImageDownloadError(null);
            setQuotationError(null);
          }}
          severity="error"
          sx={{ width: '100%' }}
        >
          {exportError || imageDownloadError || quotationError}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={exportSuccess || imageDownloadSuccess || !!quotationSuccessMessage}
        autoHideDuration={4000}
        onClose={() => {
          setExportSuccess(false);
          setImageDownloadSuccess(false);
          setQuotationSuccessMessage(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            setExportSuccess(false);
            setImageDownloadSuccess(false);
            setQuotationSuccessMessage(null);
          }}
          severity="success"
          sx={{ width: '100%' }}
        >
          {quotationSuccessMessage || (exportSuccess ? 'Products exported successfully!' : 'Images downloaded successfully!')}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductSearchPage;